import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useCustomerData } from 'src/content/pages/customer/customerData';
import { useStyles } from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';
import { enqueueSnackbar } from 'notistack';
import { useSalesPartnerData } from '../../SalesPartnerData';
import { SalesPartnerPaymentServices } from 'src/services/salesPartner/salesPartnerPaymentServices';

const paymentMethodType = [
  { id: 0, name: 'Online' },
  { id: 1, name: 'Cash' }
];

interface AddPaymentHistoryProps {
  open: boolean;
  handleClose: () => void;
  onDataAdded: (newRecord: any) => void; 
}

export const AddPaymentHistory = ({ open, handleClose, onDataAdded }: AddPaymentHistoryProps) => {

  const { bankAccountTypes } = useCustomerData();
  const { salesPartner, bankAccountsList = [] } = useSalesPartnerData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newData, setNewData] = useState<any>({});
  const [showField, setShowField] = useState(false);

  const resetForm = () => {
    setNewData({});
    setShowField(false);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const handleDataChange = (fieldName: string, value: any) => {
    setNewData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleDataChange('amount', value);
    }
  };

  const validateForm = () => {
    if (!newData.date) {
      enqueueSnackbar('Please select a date', { variant: 'error' });
      return false;
    }
    
    if (newData.paymentMode === null) {
      enqueueSnackbar('Please select a payment mode', { variant: 'error' });
      return false;
    }
    
    if (newData.paymentMode === 0 && !newData.bankAccount) {
      enqueueSnackbar('Please select a bank account for online payment', { variant: 'error' });
      return false;
    }
    
    if (newData.paymentMode === 1 && !newData.paidTo) {
      enqueueSnackbar('Please enter who the payment was made to for cash payment', { variant: 'error' });
      return false;
    }
    
    if (!newData.amount) {
      enqueueSnackbar('Please enter an amount', { variant: 'error' });
      return false;
    }
    
    if (!newData.paidBy) {
      enqueueSnackbar('Please enter who made the payment', { variant: 'error' });
      return false;
    }
    
    if (!newData.voucherNumber) {
      enqueueSnackbar('Please enter a voucher number', { variant: 'error' });
      return false;
    }
    
    return true;
  };

  const handleDataAdd = () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const formattedData = {
      id: 0,
      date: newData.date,
      paymentMethod: newData.paymentMode,
      spBankAccountId: newData.bankAccount || 0,
      amount: parseFloat(newData.amount) || 0,
      paidById: 0,
      paidByName: newData.paidBy,
      receivedById: 0,
      receivedByName: newData.paidTo || "",
      voucherNumber: newData.voucherNumber,
      note: newData.additionalNotes || '',
      salesPartnerId: salesPartner?.id || 0
    };

    SalesPartnerPaymentServices.addData(formattedData)
      .then((data) => {
        enqueueSnackbar(
          `Payment Details '${data.date} ${paymentMethodType.find((item) => item.id === data.paymentMethod)?.name}' added successfully`,
          { variant: 'success' }
        );
        onDataAdded(data); 
        resetForm();
        handleClose();
      })
      .catch((error) => {
        console.error('Error Response:', error.response?.data || error);
        enqueueSnackbar(
          `Payment Details '${newData.date} ${paymentMethodType.find((item) => item.id === newData.paymentMode)?.name}' not added successfully`,
          { variant: 'error' }
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDialogClose = () => {
    resetForm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogTitle style={useStyles.dialogTitle}>Add Payment Details</DialogTitle>
      <DialogContent style={{ ...useStyles.bodyStyle, ...useStyles.boxSize }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Select Date *
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={newData.date || ''}
              onChange={(e) => handleDataChange('date', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Payment Mode *
            </Typography>
            <Autocomplete
              options={paymentMethodType}
              getOptionLabel={(option) => option?.name || ''}
              fullWidth
              value={paymentMethodType.find((x) => x.id === newData.paymentMode) || null}
              onChange={(event, newValue) => {
                if (!newValue) {
                  setNewData((prev) => ({ ...prev, paymentMode: null }));
                  setShowField(false);
                  return;
                }

                const selectedpaymentMethodType = newValue.id;
                setNewData((prev) => ({ ...prev, paymentMode: selectedpaymentMethodType }));

                if (selectedpaymentMethodType === 0) {
                  if (!bankAccountsList || bankAccountsList.length === 0) {
                    enqueueSnackbar('Please add a bank account before selecting Online payment.', {
                      variant: 'warning'
                    });
                    setNewData((prev) => ({ ...prev, paymentMode: null }));
                    setShowField(false);
                    return;
                  }
                  setShowField(false);
                } else {
                  setShowField(true);
                }
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select Payment Mode" />}
            />
          </Grid>

          {!showField && (
            <Grid item xs={6}>
              <Typography variant="h5" style={useStyles.formTitle}>
                Select Bank Account *
              </Typography>
              <Autocomplete
                options={Array.isArray(bankAccountsList) ? bankAccountsList : []}
                getOptionLabel={(option) => option?.bankName || ''}
                fullWidth
                value={
                  Array.isArray(bankAccountsList)
                    ? bankAccountsList.find((x) => x.id === newData.bankAccount) || null
                    : null
                }
                onChange={(event, newValue) => {
                  handleDataChange('bankAccount', newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Bank Account" />
                )}
              />
            </Grid>
          )}

          {showField && (
            <Grid item xs={6}>
              <Typography variant="h5" style={useStyles.formTitle}>
                Paid To *
              </Typography>
              <TextField
                fullWidth
                value={newData.paidTo || ''}
                onChange={(e) => handleDataChange('paidTo', e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Amount *
            </Typography>
            <TextField
              fullWidth
              value={newData.amount || ''}
              onChange={handleAmountChange}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Paid By *
            </Typography>
            <TextField
              fullWidth
              value={newData.paidBy || ''}
              onChange={(e) => handleDataChange('paidBy', e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Voucher Number *
            </Typography>
            <TextField
              fullWidth
              value={newData.voucherNumber || ''}
              onChange={(e) => handleDataChange('voucherNumber', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" style={useStyles.formTitle}>
              Additional Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={newData.additionalNotes || ''}
              onChange={(e) => handleDataChange('additionalNotes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleDialogClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleDataAdd}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};